import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck } from "lucide-react";
import api from "../lib/api";
import PageHero from "../components/PageHero";
import Stars from "../components/Stars";

const CATS = [
  ["cleanliness", "Cleanliness"], ["comfort", "Comfort"], ["location", "Location"],
  ["facilities", "Facilities"], ["value", "Value"], ["overall", "Overall"],
];

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/reviews/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const sources = ["all", ...Array.from(new Set(reviews.map((r) => r.source)))];
  const shown = filter === "all" ? reviews : filter === "featured" ? reviews.filter((r) => r.featured) : reviews.filter((r) => r.source === filter);

  return (
    <div>
      <PageHero
        eyebrow="Reviews"
        title="Verified guest stories"
        subtitle="Every review is from a real, completed stay — verified and moderated by our team."
        image="https://images.unsplash.com/photo-1618140052121-39fc6db33972?q=85&w=1600"
      />

      {stats && (
        <section className="py-14 bg-sand/60">
          <div className="container-lux grid sm:grid-cols-2 lg:grid-cols-4 gap-6 items-center">
            <div className="text-center">
              <div className="font-serif text-6xl text-rose">{stats.average}</div>
              <Stars value={stats.average} size={20} className="justify-center mt-2" />
              <div className="text-sm text-stone-500 mt-2">{stats.count} verified reviews</div>
            </div>
            <div className="lg:col-span-3 grid sm:grid-cols-3 gap-4">
              {CATS.map(([k, label]) => (
                <div key={k} className="bg-white rounded-xl p-4 border border-stone-200/70">
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wide text-stone-500">{label}</span>
                    <span className="font-semibold text-forest">{stats.categories?.[k] ?? "-"}</span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-stone-100 overflow-hidden">
                    <div className="h-full bg-gold" style={{ width: `${((stats.categories?.[k] || 0) / 5) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 container-lux">
        <div className="flex flex-wrap gap-3 mb-10">
          <button onClick={() => setFilter("featured")} className={`px-5 py-2 rounded-full text-sm font-medium ${filter === "featured" ? "bg-rose text-white" : "bg-white border border-stone-200 text-stone-600"}`}>Featured</button>
          {sources.map((s) => (
            <button key={s} data-testid={`review-filter-${s}`} onClick={() => setFilter(s)}
              className={`px-5 py-2 rounded-full text-sm font-medium capitalize ${filter === s ? "bg-rose text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-rose/40"}`}>
              {s === "all" ? "All reviews" : s}
            </button>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {shown.map((r) => (
            <motion.div key={r.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="card-lux p-7" data-testid={`review-card-${r.id}`}>
              <div className="flex items-center justify-between">
                <Stars value={r.rating} />
                {r.verified && (
                  <span className="flex items-center gap-1 text-[11px] text-green-700 bg-green-50 px-2.5 py-1 rounded-full">
                    <BadgeCheck size={13} /> Verified via {r.source}
                  </span>
                )}
              </div>
              <h3 className="font-serif text-xl text-forest mt-4">{r.title}</h3>
              <p className="text-sm text-stone-600 mt-2 leading-relaxed">"{r.text}"</p>
              <div className="mt-5 flex items-center justify-between">
                <div className="text-sm"><span className="font-semibold text-forest">{r.author}</span>{r.location && <span className="text-stone-400"> · {r.location}</span>}</div>
              </div>
              {r.response && (
                <div className="mt-4 border-l-2 border-gold pl-4 bg-gold-soft/60 py-2 rounded-r-lg">
                  <div className="text-xs font-semibold text-forest">Response from The Rose Air BnB</div>
                  <p className="text-sm text-stone-600 mt-1">{r.response}</p>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
